import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ShieldCheck, LogOut } from "lucide-react";
import { toast } from "sonner";
import { HelpVideo } from "@/components/HelpVideo";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];
const homeFor = (role) => (role === "admin" ? "/admin" : role === "instructor" ? "/prof" : "/app");

const F = ({ label, req, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-slate-700">{label}{req && <span className="text-red-500"> *</span>}</Label>
    {children}
  </div>
);

/** 1º acesso: completar o cadastro e criar a senha pessoal (obrigatório antes de acessar os cursos). */
export default function FirstAccess() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const isStudent = user?.role === "student";
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasTerms, setHasTerms] = useState(false);
  const [willSign, setWillSign] = useState(false);   // contrato digital: aparece logo depois de salvar o cadastro
  const [accept, setAccept] = useState(false);
  useEffect(() => { api.get("/public/school").then((r) => { setHasTerms(!!(r.data.terms_html || r.data.privacy_html) && !r.data.contract_signature_required); setWillSign(!!r.data.contract_signature_required); }).catch(() => {}); }, []);

  useEffect(() => {
    if (user && !user.onboarding_required) { navigate(homeFor(user.role), { replace: true }); return; }
    api.get("/me/details").then((r) => {
      const p = r.data.person || {};
      setF({ name: user?.name || "", phone: user?.phone || "", profession: user?.profession || "", bio: "",
        nationality: p.nationality || "brasileiro", country: p.country || "", document: p.document || "", birth_date: p.birth_date || "",
        cep: p.cep || "", street: p.street || "", number: p.number || "", complement: p.complement || "",
        district: p.district || "", city: p.city || "", state: p.state || "", password: "", confirm: "" });
    }).catch(() => setF({ name: user?.name || "", phone: "", profession: "", bio: "", nationality: "brasileiro", country: "", document: "",
      birth_date: "", cep: "", street: "", number: "", complement: "", district: "", city: "", state: "", password: "", confirm: "" }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const up = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const foreign = f?.nationality === "estrangeiro";

  const lookupCep = async () => {
    if (foreign) return;
    const d = (f.cep || "").replace(/\D/g, "");
    if (d.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${d}/json/`).then((x) => x.json());
      if (!r.erro) setF((p) => ({ ...p, street: r.logradouro || p.street, district: r.bairro || p.district, city: r.localidade || p.city, state: r.uf || p.state }));
    } catch (e) { /* segue manual */ }
  };

  const save = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return toast.error("A confirmação não confere com a senha.");
    if (hasTerms && !accept) return toast.error("Para continuar, aceite o contrato e a política de privacidade.");
    setSaving(true);
    try {
      const { confirm, ...body } = f;
      if (!foreign) body.country = "Brasil";
      const r = await api.post("/me/onboarding", { ...body, accept_terms: accept });
      setUser(r.data);
      toast.success(r.data.contract_pending ? "Cadastro salvo! Agora falta só assinar o contrato." : "Cadastro concluído! Bem-vindo(a) à plataforma.");
      navigate(homeFor(r.data.role), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!f) return <div className="min-h-screen grid place-items-center bg-[#F8FAFC]"><Loader2 className="h-8 w-8 animate-spin text-[#232D4F]" /></div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Brand size="md" />
          <button onClick={async () => { await logout(); navigate("/login"); }} className="text-sm text-slate-500 hover:text-red-600 flex items-center gap-1.5"><LogOut className="h-4 w-4" /> Sair</button>
        </div>
        <form onSubmit={save} className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="first-access-form">
          <div className="px-6 py-5 text-white" style={{ background: "linear-gradient(120deg,#232D4F 0%,#1A2340 60%,#1E9E2A 140%)" }}>
            <h1 className="font-display text-2xl font-bold">Complete seu cadastro</h1>
            <p className="text-white/85 text-sm mt-1">Olá, {user?.name?.split(" ")[0]}! Confira seus dados e crie sua senha. Depois disso, seus cursos ficam liberados.</p>
            <HelpVideo moment="primeiro_acesso" label="Ver vídeo: como completar o cadastro" title="Como completar o seu cadastro" className="mt-2 !text-[#A7F3B0]" />
          </div>

          <div className="p-6 space-y-6">
            <section className="grid sm:grid-cols-2 gap-4">
              <F label="Nome completo" req className="sm:col-span-2">
                {user?.role === "instructor" ? (
                  <>
                    <Input value={f.name} readOnly className="bg-slate-50" data-testid="first-access-name" />
                    <p className="text-[11px] text-slate-400 mt-1">O nome foi cadastrado pela escola e consta no contrato, nos certificados e nas notas fiscais. Para corrigir, fale com a escola.</p>
                  </>
                ) : <Input value={f.name} onChange={up("name")} required />}
              </F>
              <F label="E-mail"><Input value={user?.email || ""} readOnly /></F>
              <F label="Telefone / WhatsApp" req><Input value={f.phone} onChange={up("phone")} placeholder="(00) 00000-0000" required /></F>
              <F label="Nacionalidade">
                <select value={f.nationality} onChange={up("nationality")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option value="brasileiro">Brasileiro(a)</option><option value="estrangeiro">Estrangeiro(a)</option>
                </select>
              </F>
              {foreign && <F label="País" req><Input value={f.country} onChange={up("country")} required /></F>}
              {isStudent && <F label={foreign ? "Passaporte / documento" : "CPF"} req><Input value={f.document} onChange={up("document")} placeholder={foreign ? "" : "000.000.000-00"} required /></F>}
              {isStudent && <F label="Data de nascimento" req><Input type="date" value={f.birth_date} onChange={up("birth_date")} required /></F>}
              <F label="Profissão"><Input value={f.profession} onChange={up("profession")} placeholder="Ex.: Cabeleireira" /></F>
            </section>

            {isStudent && (
              <section>
                <h2 className="font-semibold text-slate-800 mb-3">Endereço</h2>
                <div className="grid sm:grid-cols-6 gap-4">
                  <F label={foreign ? "Código postal" : "CEP"} className="sm:col-span-2"><Input value={f.cep} onChange={up("cep")} onBlur={lookupCep} placeholder={foreign ? "" : "00000-000"} /></F>
                  <F label="Rua / Logradouro" className="sm:col-span-4"><Input value={f.street} onChange={up("street")} /></F>
                  <F label="Número" className="sm:col-span-1"><Input value={f.number} onChange={up("number")} /></F>
                  <F label="Complemento" className="sm:col-span-2"><Input value={f.complement} onChange={up("complement")} /></F>
                  <F label="Bairro" className="sm:col-span-3"><Input value={f.district} onChange={up("district")} /></F>
                  <F label="Cidade" className="sm:col-span-4"><Input value={f.city} onChange={up("city")} /></F>
                  <F label={foreign ? "Estado / Província" : "Estado (UF)"} className="sm:col-span-2">
                    {foreign ? <Input value={f.state} onChange={up("state")} /> : (
                      <select value={f.state} onChange={up("state")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                        <option value="">—</option>{UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    )}
                  </F>
                </div>
              </section>
            )}

            <section className="rounded-xl border border-green-200 bg-green-50/60 p-4">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-3"><ShieldCheck className="h-4 w-4 text-[#1E9E2A]" /> Crie sua senha</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <F label="Nova senha" req><Input type="password" autoComplete="new-password" minLength={8} value={f.password} onChange={up("password")} required data-testid="first-access-password" /></F>
                <F label="Confirmar senha" req><Input type="password" autoComplete="new-password" minLength={8} value={f.confirm} onChange={up("confirm")} required /></F>
              </div>
              <p className="text-xs text-slate-500 mt-2">Mínimo de 8 caracteres. O código recebido por e-mail deixa de valer.</p>
            </section>
          </div>

          {hasTerms && (
            <div className="px-6 pb-2">
              <label className="flex items-start gap-3 rounded-xl border border-slate-200 p-4 cursor-pointer text-sm text-slate-700" data-testid="first-access-terms">
                <Checkbox checked={accept} onCheckedChange={(v) => setAccept(!!v)} className="mt-0.5" />
                <span>Li e aceito o <a href="/termos" target="_blank" rel="noreferrer" className="text-[#1E9E2A] font-semibold hover:underline">contrato e termos de uso</a> e a{" "}
                  <a href="/privacidade" target="_blank" rel="noreferrer" className="text-[#1E9E2A] font-semibold hover:underline">política de privacidade</a>.</span>
              </label>
            </div>
          )}
          {willSign && <p className="px-6 pb-2 text-sm text-slate-500" data-testid="first-access-contract-note">Depois de salvar, você vai conferir e assinar o contrato do curso. Só então o acesso aos cursos é liberado.</p>}
          <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
            <Button type="submit" disabled={saving} className="text-white px-8 h-11 font-semibold" style={{ background: "#1E9E2A" }} data-testid="first-access-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (willSign ? "Salvar e continuar" : "Salvar e acessar meus cursos")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
